import { cn } from "@istari-frontend/shared";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot as SlotPrimitive } from "radix-ui";
import * as React from "react";

export const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border font-medium w-fit whitespace-nowrap shrink-0 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary",
        secondary: "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        subtle: "border-border bg-background text-muted-foreground [a&]:hover:bg-background",
        success: "border-success/30 bg-success/10 text-success dark:bg-success/20",
        info: "border-info/30 bg-info/10 text-info dark:bg-info/20",
        warning: "border-warning/30 bg-warning/10 text-warning dark:bg-warning/20",
        danger: "border-destructive/30 bg-destructive/10 text-destructive dark:bg-destructive/20",
      },
      size: {
        xs: "px-[4px] text-[0.6875rem] [&>svg]:size-3 leading-snug rounded-sm has-[>svg]:py-0.5 has-[>svg]:px-[6px]",
        sm: "px-2 py-0.5 text-xs [&>svg]:size-3",
        md: "px-2.5 py-1 text-sm [&>svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "sm",
    },
  },
);

export type BadgeSize = VariantProps<typeof badgeVariants>["size"];

export function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? SlotPrimitive.Slot : "span";

  return <Comp data-slot="badge" className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}
